import { TrackerMapper } from "../enums"
import type { IdentifyPayload, TrackerAdapter, TrackPayload } from "./base"

export interface FirebaseTracker {
  logEvent: (name: string, properties?: Record<string, unknown>) => void | Promise<void>
  setUserId: (id: string | null) => Promise<void>
  setUserProperties: (properties: Record<string, string | null>) => Promise<void>
}

/**
 * Who the user is stays out of Google Analytics, which gets the user id only, as the API's GA4
 * proxy also makes sure. They are set to null rather than left out, which clears the values
 * earlier versions stored in Firebase on the device.
 */
const IDENTITY_PROPERTY_KEYS = new Set(["email", "name", "image", "handle"])

export interface FirebaseAdapterConfig {
  instance: FirebaseTracker
  enabled?: boolean
}

export class FirebaseAdapter implements TrackerAdapter {
  private firebaseInstance: FirebaseTracker
  private enabled: boolean

  constructor(config: FirebaseAdapterConfig) {
    this.firebaseInstance = config.instance
    this.enabled = config.enabled ?? true
  }

  initialize(): void {
    // Firebase is initialized when the instance is passed
  }

  async track({ eventName, properties }: TrackPayload): Promise<void> {
    if (!this.isEnabled()) return

    try {
      // Handle special Firebase events based on the original event code
      const code = properties?.__code as TrackerMapper | undefined
      const internalEventName = properties?.__eventName as string | undefined
      const firebaseProperties = properties ? { ...properties } : undefined
      delete firebaseProperties?.__code
      delete firebaseProperties?.__eventName

      if (code !== undefined) {
        await this.handleSpecialEvents(code, firebaseProperties, internalEventName)
      } else {
        await this.firebaseInstance.logEvent(eventName, firebaseProperties)
      }
    } catch (error) {
      console.error(`[Firebase] Failed to track event "${eventName}":`, error)
    }
  }

  private async handleSpecialEvents(
    code: TrackerMapper,
    properties?: Record<string, unknown>,
    internalEventName?: string,
  ): Promise<void> {
    switch (code) {
      case TrackerMapper.Identify: {
        // Identify is handled separately, skip here
        break
      }
      case TrackerMapper.OnBoarding: {
        if (properties?.step === 0) {
          await this.firebaseInstance.logEvent("tutorial_begin")
        } else if (properties?.done) {
          await this.firebaseInstance.logEvent("tutorial_complete")
        }
        break
      }
      case TrackerMapper.NavigateEntry: {
        await this.firebaseInstance.logEvent("select_content", {
          content_type: "entry",
          item_id: `${properties?.feedId}/${properties?.entryId}`,
        })
        break
      }
      case TrackerMapper.UserLogin: {
        await this.firebaseInstance.logEvent("login", {
          method: properties?.type as string,
        })
        break
      }
      case TrackerMapper.Register: {
        await this.firebaseInstance.logEvent("sign_up", {
          method: properties?.type as string,
        })
        break
      }
      case TrackerMapper.Subscribe: {
        let group_id
        if (properties?.listId) {
          group_id = `list/${properties.listId}/${properties.view}`
        } else if (properties?.feedId) {
          group_id = `feed/${properties.feedId}/${properties.view}`
        }
        if (group_id) {
          await this.firebaseInstance.logEvent("join_group", {
            group_id,
          })
        }
        break
      }
      case TrackerMapper.DailyRewardClaimed: {
        await this.firebaseInstance.logEvent("earn_virtual_currency", {
          virtual_currency_name: "POWER",
        })
        break
      }
      default: {
        // For other events, use the event name directly
        await this.firebaseInstance.logEvent(internalEventName || "unknown_event", properties)
      }
    }
  }

  async identify(payload: IdentifyPayload): Promise<void> {
    if (!this.isEnabled()) return

    try {
      await this.firebaseInstance.setUserId(payload.id)
    } catch (error) {
      console.error("[Firebase] Failed to identify user:", error)
    }
  }

  async setUserProperties(properties: Record<string, unknown>): Promise<void> {
    if (!this.isEnabled()) return

    try {
      // Convert properties to Firebase's expected format
      const firebaseProperties: Record<string, string | null> = {}
      for (const [key, value] of Object.entries(properties)) {
        firebaseProperties[key] =
          value === null || value === undefined || IDENTITY_PROPERTY_KEYS.has(key)
            ? null
            : String(value)
      }
      await this.firebaseInstance.setUserProperties(firebaseProperties)
    } catch (error) {
      console.error("[Firebase] Failed to set user properties:", error)
    }
  }

  async clear(): Promise<void> {
    if (!this.isEnabled()) return

    try {
      await this.firebaseInstance.setUserId(null)
      await this.firebaseInstance.setUserProperties({})
    } catch (error) {
      console.error("[Firebase] Failed to clear user data:", error)
    }
  }

  getName(): string {
    return "Firebase"
  }

  isEnabled(): boolean {
    return this.enabled
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled
  }
}
